/**
 * Jobs Board — lists all open jobs with category filter.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@design-system';

import { useJobStore } from '../../domains/jobs';

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'design', label: 'Design' },
  { value: 'product', label: 'Product' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'operations', label: 'Operations' },
  { value: 'sales', label: 'Sales' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'other', label: 'Other' },
];

function JobsPage() {
  const navigate = useNavigate();
  const { jobs, loading, error, loadJobs } = useJobStore();
  const [category, setCategory] = React.useState('');

  useEffect(() => {
    loadJobs(category || undefined);
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategoryChange = (e: { target: { value: unknown } }) => {
    setCategory(e.target.value as string);
  };

  return (
    <Box>
      <Box className="mb-6 flex items-center justify-between">
        <Typography variant="h4">Jobs Board</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/jobs/new')}
        >
          Post a Job
        </Button>
      </Box>

      <FormControl size="small" className="mb-4 min-w-[200px]">
        <InputLabel>Category</InputLabel>
        <Select
          value={category}
          label="Category"
          onChange={handleCategoryChange}
        >
          {CATEGORIES.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && (
        <Box className="flex justify-center py-8">
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      {!loading && jobs.length === 0 && (
        <Typography color="text.secondary" className="py-8 text-center">
          No open jobs yet. Be the first to post one!
        </Typography>
      )}

      <Box className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {jobs.map((job) => (
          <Card key={job._id} variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {job.title}
              </Typography>
              <Box className="mb-2 flex flex-wrap gap-1">
                <Chip label={job.category} size="small" color="primary" variant="outlined" />
                <Chip label={job.type} size="small" variant="outlined" />
                {job.remote && <Chip label="Remote" size="small" color="success" variant="outlined" />}
              </Box>
              <Typography variant="body2" color="text.secondary" className="mb-2">
                {job.description.length > 150
                  ? `${job.description.substring(0, 150)}…`
                  : job.description}
              </Typography>
              <Box className="flex flex-wrap gap-1">
                {job.skills.slice(0, 4).map((skill) => (
                  <Chip key={skill} label={skill} size="small" />
                ))}
                {job.skills.length > 4 && (
                  <Chip label={`+${job.skills.length - 4}`} size="small" />
                )}
              </Box>
            </CardContent>
            <CardActions>
              <Button size="small" onClick={() => navigate(`/jobs/${job._id}`)}>
                View Details
              </Button>
              {job.budget && (
                <Typography variant="caption" color="text.secondary" className="ml-auto pr-2">
                  Budget: {job.budget}
                </Typography>
              )}
            </CardActions>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

export default JobsPage;
